<template>
  <AddDialogWithLoader
    :dialog-title="$route.meta.title"
    :right-panel-title="translate('pb2.replacePresetView.replaceTitle')"
    :confirm-disabled="!selected"
    :scroll="scrollAmount"
    confirm-label="pb2.replacePresetView.confirmLabel"
    @add="replace"
    @scroll="scrolled"
  >
    <template #header>
      <div>
        <PbInput
          v-model="searchString"
          dynamic
          :label="translate('pb2.replacePresetView.searchLabel')"
        >
          <BaseButton
            :class="{
              'txt-muted': !searchString,
              'hover-error': !!searchString,
              'focus-error': true,
            }"
            :disabled="!searchString"
            @click="clearSearch"
          >
            <UiIcon
              icon="trash"
              size="small"
            />
          </BaseButton>
        </PbInput>
      </div>
    </template>
    <template #left>
      <TagFilters
        :route-name="(
          isGlobal
            ? routeNames.ReplaceGlobalPresetTag
            : routeNames.ReplaceLocalPresetTag
        )"
        :tag="profileStore.tags"
      />
    </template>

    <template #middleTitle>
      <TagBreadcrumb
        v-if="tag"
        :route-name="(
          isGlobal
            ? routeNames.ReplaceGlobalPresetTag
            : routeNames.ReplaceLocalPresetTag
        )"
        :tag="tag"
      />
      <template v-else>
        {{ translate('pb2.replacePresetView.recommendedPresetsTitle') }}
      </template>
      <TagViewMode />
    </template>
    <template #middle>
      <PbDefaultItem
        v-for="preset in tag.defaultPresets"
        :key="preset.defaultUid"
        v-memo="[preset.defaultUid, tag.preferences.extended]"
        :item="preset"
        :action="translate('pb2.replacePresetView.replaceWithTooltip')"
        :extended="tag.preferences.extended"
        @click="select"
      />
      <template
        v-if="profileStore.presetSearchString"
      >
        <template
          v-if="profileStore.tags.references.All.defaultPresets.size"
        >
          <PbLink
            v-if="tag.name !== 'All'"
            class="pb-add-dialog-after-results"
            exact
            :to="viewMoreDestination"
          >
            {{ translate('pb2.replacePresetView.viewMoreMessage') }}
          </PbLink>
        </template>
        <span
          v-else
          class="pb-add-dialog-after-results"
        >
          {{ translate('pb2.replacePresetView.noResultsMessage') }}
        </span>
      </template>
    </template>

    <template #right>
      <PbDefaultItem
        v-if="currentPreset"
        :item="currentPreset.data"
        disabled
      />
      {{ translate('pb2.replacePresetView.withMessage') }}
      <PbDefaultItem
        v-if="selected"
        :item="selected"
        disabled
      />
      <template v-else>
        {{ translate('pb2.replacePresetView.noSelectionMessage') }}
      </template>
    </template>
  </AddDialogWithLoader>
</template>

<script setup>
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useProfileStore from '@/stores/profile.store'
import usePresetStore from '@/stores/preset.store'
import AddDialogWithLoader from '@/components/AddDialog/AddDialogWithLoader.vue'
import PbInput from '@/components/PbInput.vue'
import useIsGlobal from '@/composables/useIsGlobal'
import {
  computed,
  toRef,
  ref,
  watch,
} from 'vue'
import TagFilters from '@/components/TagFilters.vue'
import TagBreadcrumb from '@/components/TagBreadcrumb.vue'
import PbDefaultItem from '@/components/PbDefaultItem/PbDefaultItem.vue'
import routeNames from '@/router/names'
import PbLink from '@/components/PbLink.vue'
import useDestination from '@/composables/useDestination'
import usePresetSearch from '@/composables/usePresetSearch'
import { usePreset } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'
import TagViewMode from './TagViewMode.vue'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const profileStore = useProfileStore()
const presetStore = usePresetStore()

const selected = ref(null)

const tag = toRef(profileStore, 'tag')

const {
  searchInput: searchString,
  clear: clearSearch,
  scrolled,
  scrollAmount,
} = usePresetSearch()

watch(tag, () => {
  // When the tag changes, reset the scroll
  scrollAmount.value = 0
})

const viewMoreTo = computed(() => ({
  name: isGlobal.value ? routeNames.ReplaceGlobalPresetTag : routeNames.ReplaceLocalPresetTag,
  params: {
    tag: profileStore.tags.references.All.pathArr,
  },
}))

const viewMoreDestination = useDestination(viewMoreTo)

const currentPreset = usePreset()

function replace() {
  if (isGlobal.value) {
    return presetStore.replaceGlobal({
      // Keep only the defaultUid, other properties will be handled by the profile-sdk
      defaultUid: selected.value.defaultUid,
    })
  }
  return presetStore.replaceLocal({
    // Keep only the defaultUid, other properties will be handled by the profile-sdk
    defaultUid: selected.value.defaultUid,
  })
}

function select(preset) {
  selected.value = preset
}
</script>
